import React, { useState, useEffect, useRef } from 'react';

export default function JackpotCounter(props) {
    const [isReady, setIsReady] = useState(false);          // gate for first render after server response arrives
    const displayValueRef = useRef(null);                   // current animated value, updated every frame without triggering re-render
    const targetRef = useRef(null);                         // latest server value, animation never exceeds this
    const speedRef = useRef(0);                             // units per ms, recalculated on each server response
    const animationFrame = useRef(null);                    // holds rAF id for cleanup on unmount
    const isVisibleRef = useRef(true); // tracks if component is in viewport
    const containerRef = useRef(null);  // ref to the DOM element to observe
    const valueNodeRef = useRef(null);  // ref to span whose textContent we mutate directly (bypasses React render)
    const value = props.jackpotsUkupno?.iznos;
    const getJackpotTotal = props.fetchJackpots;

    // Direct DOM write — avoids React re-render on every animation frame (~60fps).
    const writeToDom = (val) => {
        if (valueNodeRef.current) {
            valueNodeRef.current.textContent = casino.utils.numberWithCurrencyFromated(val);
        }
    };

    const POLL_INTERVAL = 30000; // change to 30000 in production

    // observe visibility
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                isVisibleRef.current = entry.isIntersecting;
            },
            { threshold: 0.015 } // trigger when at least 1.5% is visible
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // ---- TEST: simulates server responding every POLL_INTERVAL ----
    // delete this block in production and uncomment the production block below
    //const [serverValue, setServerValue] = useState(4500000);
    //useEffect(() => {
    //    const tick = () => setServerValue(v => v + Math.random() * 100 + 100); // random increment between 100-600
    //    const firstTick = setTimeout(tick, 100); // fire almost immediately on mount
    //    const interval = setInterval(tick, POLL_INTERVAL);
    //    return () => {
    //        clearTimeout(firstTick);
    //        clearInterval(interval);
    //    };
    //}, []);
    // ---- END TEST ----

    // ---- PRODUCTION: uncomment this and change serverValue -> value prop everywhere ----
    useEffect(() => {
        let firstTick;
        let interval;

        const startPolling = () => {
            getJackpotTotal();
            interval = setInterval(getJackpotTotal, 30000);
        };

        const stopPolling = () => {
            clearTimeout(firstTick);
            clearInterval(interval);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopPolling();
            } else {
                // Restart polling kad se korisnik vrati
                startPolling();
            }
        };

        // Prvi fetch nakon 11 sekundi, zatim svakih 30s
        firstTick = setTimeout(() => {
            startPolling();
        }, 11000);

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // reacts to new server value arriving
    // in production: change [serverValue] dep and references to [value] prop
    useEffect(() => {
        if (value === null || value === undefined) return;

        // Parse European-formatted string: "2.440.240,71" 
        const parsed = typeof value === 'string'
            ? Number(value.replace(/\./g, '').replace(',', '.'))
            : Number(value);

        targetRef.current = parsed; // update the ceiling the animation won't exceed

        if (displayValueRef.current === null) {
            // first server response — snap display to real value, animation starts on next response
            displayValueRef.current = parsed - 2; // starts immidiately
            writeToDom(parsed - 2);
            setIsReady(true);
            speedRef.current = 2 / POLL_INTERVAL;
            return;
        }

        if (parsed < displayValueRef.current) {
            // jackpot was won and reset to base value — snap immediately, no animation
            displayValueRef.current = parsed;
            writeToDom(parsed);
            speedRef.current = 0;
            return;
        }

        // normal case � calculate speed to cover the diff evenly over the poll interval
        const diff = parsed - displayValueRef.current;
        speedRef.current = diff / POLL_INTERVAL;
    }, [value]);

    // animation loop � runs every frame from mount until unmount
    useEffect(() => {
        let lastTime = null;

        const step = (now) => {
            if (lastTime !== null && displayValueRef.current !== null && isVisibleRef.current && !props.igraOtvorena) {
                const delta = now - lastTime; // ms since last frame, typically ~16ms at 60fps
                const next = displayValueRef.current + speedRef.current * delta;

                // clamp so we never overshoot the server value
                displayValueRef.current = targetRef.current !== null
                    ? Math.min(next, targetRef.current)
                    : next;

                writeToDom(displayValueRef.current);
            }
            lastTime = now;
            animationFrame.current = requestAnimationFrame(step);
        };

        animationFrame.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(animationFrame.current); // cleanup on unmount
    }, []);

    // nothing to show until first server response
    if (!isReady) return null;

    return (
        <div ref={containerRef}>
            <span ref={valueNodeRef} />
        </div>
    );
}